import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Calendar, Clock, Users, XCircle, CheckCircle2, AlertCircle, Phone, Mail } from "lucide-react";
import { PiktableLoader } from "@/components/PiktableLoader";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useEstablishmentBySlug } from "@/contexts/EstablishmentContext";

export default function CancelReservation() {
  const { token, slug } = useParams<{ token: string; slug?: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { establishment, loading: establishmentLoading, error: establishmentError } = useEstablishmentBySlug(slug);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [reservation, setReservation] = useState<any>(null);
  const [sameDayBlocked, setSameDayBlocked] = useState(false);
  const [blockedShowPhone, setBlockedShowPhone] = useState(false);
  const [blockedShowEmail, setBlockedShowEmail] = useState(false);
  const [blockedActionsPhone, setBlockedActionsPhone] = useState<string | null>(null);
  const [blockedActionsEmail, setBlockedActionsEmail] = useState<string | null>(null);

  // Vérifier le token et récupérer la réservation
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        toast({
          title: "Erreur",
          description: "Token invalide",
          variant: "destructive",
        });
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke("get-reservation-by-token", {
          body: { token, action: "cancel" },
        });

        // Helper: handle structured error payload
        const handlePayload = (payload: { error?: string; errorType?: string; showPhone?: boolean; showEmail?: boolean; blockedActionsPhone?: string | null; blockedActionsEmail?: string | null } | null) => {
          if (payload?.errorType === 'same_day_cancellation_blocked') {
            setSameDayBlocked(true);
            setBlockedShowPhone(payload.showPhone === true);
            setBlockedShowEmail(payload.showEmail === true);
            setBlockedActionsPhone(payload.blockedActionsPhone ?? null);
            setBlockedActionsEmail(payload.blockedActionsEmail ?? null);
            return;
          }
          toast({
            title: "Erreur",
            description: payload?.error || "Impossible de trouver votre réservation",
            variant: "destructive",
          });
        };

        if (error) {
          console.error("Token validation error:", error);
          let payload: { error?: string; errorType?: string; showPhone?: boolean; showEmail?: boolean; blockedActionsPhone?: string | null; blockedActionsEmail?: string | null } | null = null;
          try {
            const ctx = (error as any).context;
            if (ctx && typeof ctx.json === 'function') {
              payload = await ctx.json();
            } else if (ctx && typeof ctx.text === 'function') {
              const txt = await ctx.text();
              try { payload = JSON.parse(txt); } catch { payload = { error: txt }; }
            }
          } catch (e) {
            console.warn("Could not parse error context:", e);
          }
          handlePayload(payload);
          return;
        }

        if (!data?.success || !data?.reservation) {
          handlePayload(data ?? null);
          return;
        }

        setReservation(data.reservation);
      } catch (error) {
        console.error("Error validating token:", error);
        toast({
          title: "Erreur",
          description: "Une erreur est survenue",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token, toast]);

  const handleCancel = async () => {
    setSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke("cancel-reservation-public", {
        body: { token },
      });

      if (error) throw error;

      setSuccess(true);
      setShowConfirmDialog(false);
      toast({
        title: "Réservation annulée",
        description: "Votre réservation a été annulée avec succès. Un email de confirmation vous a été envoyé.",
      });

      // Rediriger vers la page de réservation avec le slug si disponible
      const redirectPath = slug ? `/${slug}` : "/";
      setTimeout(() => {
        navigate(redirectPath);
      }, 3000);
    } catch (error: any) {
      console.error("Error cancelling reservation:", error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'annuler la réservation",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Afficher erreur si établissement non trouvé (seulement si slug fourni)
  if (slug && establishmentError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md text-center p-8">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Établissement non trouvé</h2>
          <p className="text-muted-foreground">
            L'établissement "{slug}" n'existe pas ou n'est plus actif.
          </p>
        </Card>
      </div>
    );
  }

  if (loading || establishmentLoading) {
    return <PiktableLoader message="Chargement..." />;
  }

  // Affichage si annulation le jour même est bloquée
  if (sameDayBlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md text-center p-8">
          <XCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Annulation non disponible</h2>
          <p className="text-muted-foreground mb-6">
            L'annulation le jour même n'est pas autorisée par cet établissement. Veuillez nous contacter directement pour annuler votre réservation.
          </p>
          {(() => {
            const phone = blockedActionsPhone || establishment?.phone || null;
            const email = blockedActionsEmail || establishment?.email || null;
            const showPhone = blockedShowPhone && !!phone;
            const showEmail = blockedShowEmail && !!email;
            if (!showPhone && !showEmail) return null;
            return (
              <div className="space-y-2 text-sm">
                {showPhone && (
                  <div className="flex items-center justify-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <a href={`tel:${phone}`} className="text-primary hover:underline">{phone}</a>
                  </div>
                )}
                {showEmail && (
                  <div className="flex items-center justify-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <a href={`mailto:${email}`} className="text-primary hover:underline">{email}</a>
                  </div>
                )}
              </div>
            );
          })()}
          <Button
            variant="outline"
            onClick={() => navigate(slug ? `/${slug}` : "/")}
            className="mt-6"
          >
            Retour
          </Button>
        </Card>
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Erreur</CardTitle>
            <CardDescription>Impossible de trouver votre réservation</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-6 w-6 text-green-500" />
              <CardTitle>Réservation annulée</CardTitle>
            </div>
            <CardDescription>
              Votre réservation a été annulée avec succès. Vous allez être redirigé...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const backPath = slug ? `/${slug}` : "/";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="flex items-center gap-2">
            <XCircle className="h-6 w-6 text-destructive" />
            <CardTitle>Annuler ma réservation</CardTitle>
          </div>
          <CardDescription>
            Êtes-vous sûr de vouloir annuler cette réservation ?
            {establishment?.name && ` (${establishment.name})`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-muted p-6 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Client</span>
              <span className="text-sm">
                {reservation.customers?.first_name} {reservation.customers?.last_name}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Date</span>
              </div>
              <span className="text-sm">
                {format(new Date(reservation.date), "dd MMMM yyyy", { locale: fr })}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Heure</span>
              </div>
              <span className="text-sm">{reservation.start_time.substring(0, 5)}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Personnes</span>
              </div>
              <span className="text-sm">{reservation.number_of_people}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Numéro de confirmation</span>
              <code className="text-xs bg-background px-2 py-1 rounded">
                {reservation.id.substring(0, 8)}...
              </code>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate(backPath)}
              className="flex-1"
            >
              Retour
            </Button>
            <Button
              variant="destructive"
              onClick={() => setShowConfirmDialog(true)}
              disabled={submitting}
              className="flex-1"
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Annuler la réservation
            </Button>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Êtes-vous absolument sûr ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Votre réservation sera définitivement annulée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Non, conserver</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              className="bg-destructive hover:bg-destructive/90"
            >
              Oui, annuler
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
